import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowLeft, Smartphone } from "lucide-react";

const CLE = "depuis_app";

const enModeApp = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

/* Le pont entre le site et l'application. Une famille qui part de son espace vers le
   planning ou une inscription se retrouvait sur le site, sans chemin pour revenir. Ce
   bouton flottant la ramène à l'application d'un tap — il n'apparaît qu'à ceux qui
   viennent de l'application (ou l'utilisent installée), jamais aux simples visiteurs. */
export const marquerDepuisApp = () => {
  try { sessionStorage.setItem(CLE, "1"); } catch { /* navigation privée */ }
};

export const RetourApp = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [ouverture, setOuverture] = useState(false);

  useEffect(() => {
    // Seulement dans l'application INSTALLÉE : sur le site ouvert dans un navigateur, le
    // menu a déjà « Mon espace », et ce bouton flottant cachait le contenu (école,
    // 09.10.2026).
    setVisible(enModeApp() && !pathname.startsWith("/espace")
      && !pathname.startsWith("/console") && !pathname.startsWith("/prof") && !pathname.startsWith("/direction"));
  }, [pathname]);

  // Revenu sur cette page par le geste « retour » (page gardée en mémoire par le
  // navigateur) : le bouton reprend son libellé.
  useEffect(() => {
    const remettre = () => setOuverture(false);
    window.addEventListener("pageshow", remettre);
    return () => window.removeEventListener("pageshow", remettre);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("depuis-app", visible);
    return () => document.body.classList.remove("depuis-app");
  }, [visible]);

  if (!visible) return null;
  // Un vrai chargement de page (pas un lien interne) : l'iPhone remet alors le zoom et le
  // défilement à zéro, et l'application s'affiche entière avec sa barre d'onglets.
  // L'école trouvait le bouton lent (09.10.2026) : la bulle de l'assistant avalait la
  // plupart des taps (voir AssistantSite.jsx et index.css). Le tap part maintenant tout de
  // suite (`touch-action: manipulation` : aucune attente d'un double tap) et le bouton
  // répond aussitôt « Ouverture… » pendant que l'app se charge. Sur une seule ligne : posé
  // à mi-largeur (left-1/2), il se coupait en deux lignes et montait sous l'assistant.
  return (
    <a href="/espace" data-testid="retour-app" onClick={() => setOuverture(true)}
      className="fixed z-[60] left-1/2 -translate-x-1/2 inline-flex items-center gap-2 whitespace-nowrap rounded-full px-5 py-3 text-sm font-semibold text-white shadow-lg active:scale-95 transition-transform"
      style={{ bottom: "calc(1rem + env(safe-area-inset-bottom, 0px))", background: "#0F1B2D",
               touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}>
      <Smartphone size={16} /> {ouverture ? "Ouverture…" : "Retour à mon application"}
    </a>
  );
};

/* « ← Retour à l'app », en haut d'une page du site ouverte DEPUIS l'application (Jen,
   01.11.2026 : dans l'app installée, il n'y a pas de bouton « retour » du navigateur).
   L'application marque ces liens `app=1` (espace/Lien.jsx, `versPageDuSite`) ; la page pose
   simplement <RetourApp /> (export par défaut, sans props) en haut de son contenu. Sans
   `app=1` — lien d'un e-mail, visiteur du site —, rien ne s'affiche.
   On revient en arrière quand l'historique mène à l'application (comme le geste
   « retour »), sinon on ouvre l'application (/espace). Tant que ce bouton est à l'écran, la
   pastille flottante « Retour à mon application » (RetourApp ci-dessus) s'efface : un seul
   bouton pour la même chose (index.css, body.retour-app-haut). */
const depuisLApp = () => {
  const idx = window.history.state?.idx;
  if (typeof idx === "number" && idx > 0) return true;   // arrivée par un lien de l'app, sans recharger
  try {
    const d = new URL(document.referrer);
    return d.origin === window.location.origin && d.pathname.startsWith("/espace") && window.history.length > 1;
  } catch {
    return false;
  }
};

export default function BoutonRetourApp() {
  const { search } = useLocation();
  const visible = new URLSearchParams(search).get("app") === "1";
  const bouton = useRef(null);

  useEffect(() => {
    const el = bouton.current;
    if (!visible || !el) return undefined;
    const marquer = (oui) => document.body.classList.toggle("retour-app-haut", oui);
    marquer(true);
    let guetteur;
    if (typeof IntersectionObserver !== "undefined") {
      guetteur = new IntersectionObserver(([e]) => marquer(e.isIntersecting));
      guetteur.observe(el);
    }
    return () => { guetteur?.disconnect(); marquer(false); };
  }, [visible]);

  if (!visible) return null;
  const revenir = () => {
    if (depuisLApp()) window.history.back();
    else window.location.assign("/espace");
  };
  return (
    <div className="mb-6">
      <button ref={bouton} type="button" onClick={revenir} data-testid="retour-app-haut"
        className="inline-flex items-center gap-1.5 rounded-full pl-3.5 pr-4 py-2.5 text-[14px] font-semibold text-white shadow-md active:scale-95 transition-transform"
        style={{ background: "#0F1B2D", touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}>
        <ArrowLeft size={16} /> Retour à l'app
      </button>
    </div>
  );
}
